import { inject } from '@angular/core';
import { ClubService } from '@core/services';
import { GetTeamId } from '@core/utils';
import { catchError, defaultIfEmpty, map, shareReplay } from 'rxjs/operators';
import { GroupEntity, Pagination, RoomEntity, UserEntity, UserListEntity } from '@core/entities';
import { plainToClass, plainToInstance } from 'class-transformer';
import { defer, lastValueFrom, Observable, of } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FilterQuery } from '@core/types';

@UntilDestroy()
export class UseClub {
  private readonly _clubService = inject(ClubService);

  getUpcomingBirthdays() {
    return this._clubService.birthdays(GetTeamId()).pipe(
      map((response) =>
        response.map((item: UserEntity) => {
          item.isJubilee = false;
          item.isBirthday = true;
          return plainToClass(UserEntity, item);
        }),
      ),
    );
  }

  getUpcomingJubilees() {
    return this._clubService.jubilees(GetTeamId()).pipe(
      map((response) =>
        response.map((item: any) => {
          (item.user as UserEntity).isJubilee = true;
          (item.user as UserEntity).isBirthday = false;
          return plainToClass(UserEntity, item.user as UserEntity);
        }),
      ),
    );
  }

  getAllMembers(pagination: Pagination, filterQuery: FilterQuery) {
    return this._clubService.findClubMembers(pagination, filterQuery).pipe(
      map((res) => {
        return plainToInstance(UserListEntity, res);
      }),
    );
  }

  getClubMembers(): Promise<UserEntity[]> {
    const teamUsers$ = defer(() => this._clubService.teamUsers(GetTeamId())).pipe(
      map((response) => response.map((item: any) => plainToInstance(UserEntity, item))),
      catchError((error) => {
        console.error('Error fetching team users:', error);
        return of([]);
      }),
      shareReplay(1),
      defaultIfEmpty([]),
    );

    return lastValueFrom(teamUsers$);
  }

  getClubGroupsRoomsAndUsers(): Observable<{ users: UserEntity[]; groups: GroupEntity[]; rooms: RoomEntity[] }> {
    return this._clubService.groupsAndUsers(+GetTeamId()).pipe(
      map((response) => {
        const users = response.users.map((user: UserEntity) => plainToClass(UserEntity, user)) as UserEntity[];
        return {
          users,
          groups: response.groups.map((group: GroupEntity) => plainToClass(GroupEntity, group)) as GroupEntity[],
          rooms: response.rooms.map((room: RoomEntity) => plainToClass(RoomEntity, room)) as RoomEntity[],
        };
      }),
    );
  }
}
